import React, { useCallback, useState } from 'react';

import Container from '../../components/container/Container';
import { exitApp } from '../../hooks/utils';
import Input from '../../components/forms/Input';
import SingleSelect from '../../components/forms/SingleSelect';
import FeatherIcon from 'feather-icons-react';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import SecondaryButton from '../../components/buttons/SecondaryButton';
import { useSettings } from '../../hooks/queryHooks';
import { useCreateTuUserMutation } from '../../hooks/mutationHooks';
import { ICreateTuUserParams, IErrorData } from '../../domain/services/IConnectApi';
import { useHistory, useLocation } from 'react-router';
import Spinner from '../../components/spinner/Spinner';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDeviceRisk } from '../../hooks/useDeviceRisk';
import { Trans } from 'react-i18next';
import Link from '../../components/links/Link';
import { EventTypes } from '../../utils/applicationInsights';
import { trackEvent } from '../../App';
import TuAddress from './TuAddress';

const titleDropdown = [
  { label: 'Mr', value: 'Mr' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Ms', value: 'Ms' },
  { label: 'Miss', value: 'Miss' },
  { label: 'Dr', value: 'Dr' },
  { label: 'Prof', value: 'Prof' }
];

export type IValues = Omit<ICreateTuUserParams, 'url' | 'applicationId' | 'userId' | 'invitationId'>;
interface LocationState {
  hasFailedValidation?: boolean;
}

const AccountLookup = () => {
  const { state: { hasFailedValidation = null } = {} } = useLocation<LocationState>();
  const history = useHistory();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [invitationFailedValidation, setInvitationFailedValidation] = useState(hasFailedValidation);
  const blackBox = useDeviceRisk().blackbox;
  const [hasLivedInPrevUkAddr, setHasLivedInPrevUkAddr] = useState<boolean>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [values, setValues] = useState<IValues>({
    address: {} as IValues['address'],
    previousAddress: {} as IValues['address'],
    title: titleDropdown[0].value,
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    emailAddress: '',
    deviceRisk: {
      blackBox: ''
    }
  });
  const scrollElemIntoView = useCallback((el: HTMLElement | null) => {
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  const { mutateAsync, isPending, error } = useCreateTuUserMutation();

  const err = error as AxiosError;
  const errorsData = err?.response?.data as IErrorData;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInvitationFailedValidation(false);
    const form = e.currentTarget;
    const isFormDataValid = form.checkValidity();
    if (!isFormDataValid) {
      for (const elem of form.elements) {
        if (elem.tagName === 'INPUT') {
          const input = elem as HTMLInputElement;
          const isValid = input.checkValidity();
          if (!isValid) {
            input.focus();
            break;
          }
        }
      }
      return;
    }

    trackEvent({ type: EventTypes.CIS_SUBMIT_PERSONAL_DETAILS });

    if (hasLivedInPrevUkAddr) {
      trackEvent({ type: EventTypes.CIS_PREVIOUS_UK_ADDRESS });
    }
    try {
      const response = await mutateAsync({
        ...values,
        previousAddress: hasLivedInPrevUkAddr === false ? undefined : values.previousAddress,
        deviceRisk: { blackBox }
      });
      const { status } = response || {};
      if (status === 'OtpRequired') history.push('/otp');
      else if (status === 'KbaRequired') history.push('/kba');
      else if (status === 'Positive') history.push('/tu-end-success');
      else history.push('/bank-select', { hasFailedVerification: true });
    } catch (err) {
      const errorsData = (err as AxiosError)?.response?.data as IErrorData;
      if (/velocity checks failed/i.test(errorsData?.message ?? '') && errorsData?.status === 'Negative') {
        history.push('/bank-select', { hasFailedVerification: true });
      }
    }
  };

  if (isPending) {
    return <Spinner text="Verifying.." />;
  }

  const isValidationError =
    /validation process was negative/i.test(errorsData?.message ?? '') && errorsData?.status === 'Negative';

  return (
    <Container className="flex flex-col">
      <h1 className="text-md sm:text-lg font-semi-bold uppercase text-primary-2">Account lookup</h1>
      <p className="text-base font-normal text-primary-2">
        We’ll use the information you provide to securely access your credit report and check for financial accounts
        linked to you.
        <br />
        This helps us build a complete picture of your finances.
      </p>
      <div className={`text-negative ${errorsData || (isValidationError && 'mt-2')}`}>
        <span aria-live="assertive" aria-atomic="true" className="font-medium">
          {errorsData?.title && errorsData?.title}
          {isValidationError &&
            'We couldn’t find a match for your account. Please check and update your details before trying again.'}
          {invitationFailedValidation &&
            'We couldn’t find a match for your account. Please enter your details to continue.'}
        </span>
        <ol className="px-5">
          {err?.status === 400 &&
            errorsData?.errors &&
            Object.entries(errorsData?.errors).map(([key, value]) => {
              if (key === 'request') return null;
              return (
                <li className="list-disc mt-1" key={key}>
                  {key === 'invitationId' ? 'Invalid URL params: invitation_id' : value.at(0)}
                </li>
              );
            })}
        </ol>
      </div>
      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-4 text-primary-2 flex flex-col justify-between grow h-max"
        ref={form => {
          if (!form) return;
          const isValid = Array.from(form.getElementsByTagName('input')).every(
            input => input.willValidate && input.validity.valid
          );
          if (isFormValid !== isValid) setIsFormValid(isValid);
          if (hasLivedInPrevUkAddr == undefined) setIsFormValid(false);
        }}
      >
        <div>
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            <legend className="font-normal text-md mb-3">Personal details</legend>
            <div className="relative max-w-26 mb-4 sm:col-span-2">
              <div aria-hidden="true" className="pointer-events-none absolute right-6 top-11">
                <FeatherIcon icon="chevron-down" className="text-gray-500" />
              </div>
              <span aria-hidden="true" className="mb-2 inline-block">
                Title
              </span>
              <SingleSelect
                label="Title"
                value={values.title}
                onChangeValue={e => {
                  setValues(prev => ({ ...prev, title: e.target.value }));
                }}
                required={true}
                className="h-12 px-4 w-36"
                options={titleDropdown}
              />
            </div>
            <Input
              autoFocus
              label="First name"
              placeholder="John"
              required={true}
              pattern="^[a-zA-Z‘\- ]*$"
              value={values.firstName}
              onChange={e => setValues(prev => ({ ...prev, firstName: e.target.value }))}
              title="Only letters, spaces, apostrophes, and hyphens allowed."
            />
            <Input
              label="Middle name (optional)"
              placeholder="Don"
              pattern="^[a-zA-Z‘\- ]*$"
              value={values.middleNames}
              onChange={e => setValues(prev => ({ ...prev, middleNames: e.target.value || undefined }))}
              title="Only letters, spaces, apostrophes, and hyphens allowed."
            />
            <Input
              label="Last name"
              placeholder="Smith"
              required={true}
              pattern="^[a-zA-Z‘\- ]*$"
              value={values.lastName}
              onChange={e => setValues(prev => ({ ...prev, lastName: e.target.value }))}
              title="Only letters, spaces, apostrophes, and hyphens allowed."
            />
            <Input
              label="Email"
              placeholder="john@gmail.com"
              type="email"
              required={true}
              pattern="^[\u00c0-\u01ffa-zA-Z0-9_\.\-]+@([\u00c0-\u01ffa-zA-Z0-9\-]+\.)+[a-zA-Z]{2,10}$"
              value={values.emailAddress}
              onChange={e => setValues(prev => ({ ...prev, emailAddress: e.target.value }))}
              title="Invalid, only _, ., - are allowed."
            />
            <Input
              type="tel"
              label="Mobile phone number"
              required={true}
              placeholder="07123456789"
              pattern="^0\d{9,10}$"
              minLength={10}
              maxLength={11}
              value={values.phoneNumber}
              onChange={e => setValues(prev => ({ ...prev, phoneNumber: e.target.value }))}
              title="should start with '0' and be 10-11 digits long"
              icon={<FeatherIcon icon="phone" className="text-gray-500" strokeWidth={0.9} />}
            />
            <Input
              required={true}
              type="date"
              className={values.dateOfBirth ? '#101010' : 'text-gray-500'}
              label="Date of birth"
              min={moment().subtract(120, 'years').format('YYYY-MM-DD')}
              onChange={e => setValues(prev => ({ ...prev, dateOfBirth: e.target.value }))}
              value={values.dateOfBirth}
              max={moment().subtract(18, 'years').format('YYYY-MM-DD')}
            />
          </fieldset>

          <TuAddress values={values} setValues={setValues} addressType="current" />

          {Object.keys(values.address).length > 0 ? (
            <div ref={scrollElemIntoView} className="flex flex-col mb-2">
              <div>
                Have you lived at any other <strong>UK address</strong> within the last 3 years?
              </div>
              <span className="text-gray-500 text-sm">
                We may need your previous <strong>UK address</strong> to help find your credit report.
              </span>
              <div className="relative max-w-44">
                <div aria-hidden="true" className="pointer-events-none absolute right-2 top-5">
                  <FeatherIcon icon="chevron-down" className="text-gray-500" />
                </div>
                <SingleSelect
                  label="hasLivedInPrevUkAddr"
                  options={[
                    { label: 'Select an option', value: '' },
                    { label: 'Yes', value: true },
                    { label: 'No', value: false }
                  ]}
                  className="h-12 px-4 w-44! max-w-44! mt-2"
                  value={hasLivedInPrevUkAddr}
                  onChangeValue={e => {
                    const val = e.currentTarget.value;
                    const state = val == '' ? undefined : val == 'true' ? true : false;
                    setHasLivedInPrevUkAddr(state);
                  }}
                />
              </div>
            </div>
          ) : null}
          {hasLivedInPrevUkAddr === true && (
            <div ref={scrollElemIntoView}>
              <TuAddress values={values} setValues={setValues} addressType="previous" />
            </div>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">
            <Trans>
              By clicking Continue, you agree to the use of address lookup services provided by{' '}
              <Link href="https://support.loqate.com/eula" target="_blank">
                Loqate
              </Link>{' '}
              and credit report check services provided by{' '}
              <Link href="https://www.transunion.co.uk/legal/privacy-centre/pc-bureau" target="_blank">
                TransUnion
              </Link>
              , and you authorise Atto to access your credit report information in accordance with the Atto{' '}
              <Link href="https://www.atto.co/policies/privacy-policy" target="_blank">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://www.atto.co/policies/terms-of-use" target="_blank">
                Terms & Conditions
              </Link>
              .
            </Trans>
          </p>
          <div
            className={`
            mt-4 grid grid-flow-row p-4 sm:px-0 sm:pb-0 gap-y-2 sm:gap-0 sm:grid-cols-auto 
            sm:grid-flow-col w-full bg-white border-t sm:border-0 border-gray-300 
            fixed sm:static bottom-0 left-0 sm:justify-between
          `}
          >
            <PrimaryButton disabled={isFormValid === false} type="submit">
              {t('Continue')}
            </PrimaryButton>
            {settings?.redirectUrl && (
              <SecondaryButton onClick={() => exitApp()} className="border-none order-last sm:order-none">
                {t('Abort & return to partner')}
              </SecondaryButton>
            )}
          </div>
        </div>
      </form>
    </Container>
  );
};

export default AccountLookup;
